/**
 * Teslyn's public legal pages (`/privacy`, `/terms`): Braynewave's privacy policy and terms of use for Teslyn Cloud.
 * Google's OAuth app needs both to open without signing in, on any portal address, so they need no tenant and no
 * session (main.tsx shows them before the tenant is resolved). The text is Markdown in `content/legal/`, bundled at
 * build time as the help pages are (ADR-012), with flat front matter: `title`, `version`, `updated` (yyyy-mm-dd).
 */

export type LegalDoc = 'privacy' | 'terms';

export const LEGAL_PATH: Readonly<Record<LegalDoc, string>> = { privacy: '/privacy', terms: '/terms' };

export const LEGAL_LABEL: Readonly<Record<LegalDoc, string>> = {
  privacy: 'Privacy policy',
  terms: 'Terms of use',
};

/** Which legal page this address is, or null. A trailing slash is allowed (`/privacy/`). */
export function legalDocFor(pathname: string): LegalDoc | null {
  const p = pathname.replace(/\/+$/, '');
  if (p === LEGAL_PATH.privacy) return 'privacy';
  if (p === LEGAL_PATH.terms) return 'terms';
  return null;
}

/**
 * The query to carry between the sign-in page and the legal pages: only `?tenant=` (preview hosts choose the tenant
 * with it), never a returned sign-in error or code.
 */
export function keptQuery(search: string): string {
  const t = new URLSearchParams(search).get('tenant');
  return t ? `?tenant=${encodeURIComponent(t)}` : '';
}

/** Address of a legal page, keeping `?tenant=` from `search`. */
export function legalHref(doc: LegalDoc, search = ''): string {
  return LEGAL_PATH[doc] + keptQuery(search);
}

/** Address of the sign-in page, keeping `?tenant=` from `search`. */
export function signInHref(search = ''): string {
  return '/' + keptQuery(search);
}

export interface LegalText {
  readonly doc: LegalDoc;
  readonly title: string;
  readonly version: string;
  /** yyyy-mm-dd */
  readonly updated: string;
  /** Markdown, with `##` section headings (the page's `h1` is the title). */
  readonly body: string;
}

const FRONT = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/** Parse one legal page's Markdown. Throws when a front matter key is missing, so a bad file fails the tests. */
export function parseLegal(doc: LegalDoc, text: string): LegalText {
  const m = FRONT.exec(text);
  const keys = new Map<string, string>();
  for (const line of (m?.[1] ?? '').split(/\r?\n/)) {
    const i = line.indexOf(':');
    if (i > 0) keys.set(line.slice(0, i).trim(), line.slice(i + 1).trim());
  }
  const need = (k: string): string => {
    const v = keys.get(k);
    if (!v) throw new Error(`content/legal/${doc}.md: front matter "${k}" is missing`);
    return v;
  };
  const title = need('title');
  const version = need('version');
  const updated = need('updated');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(updated)) {
    throw new Error(`content/legal/${doc}.md: "updated" must be yyyy-mm-dd, not ${updated}`);
  }
  return {
    doc,
    title,
    version,
    updated,
    body: m ? text.slice(m[0].length) : text,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-10-25` as `25 Oct 2026` (brand book: dates). */
export function formatLegalDate(iso: string): string {
  const [y, mo, d] = iso.split('-').map(Number) as [number, number, number];
  return `${d} ${MONTHS[mo - 1] ?? ''} ${y}`;
}

const RAW = import.meta.glob('../../../../content/legal/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/** The bundled text of one legal page. */
export function legalText(doc: LegalDoc): LegalText {
  const entry = Object.entries(RAW).find(([path]) => path.endsWith(`/${doc}.md`));
  if (!entry) throw new Error(`content/legal/${doc}.md is not bundled`);
  return parseLegal(doc, entry[1]);
}
